import axios from "axios";
import { defineStore } from "pinia";

export class MarketCartItem {
    id!: number;
    nom!: string;
    montant!: number;
    montantRemise!: number;
    sousTotal!: number;
    total!: number;
    quantite!: number;
    image?: string;
    echeance?: number;
    typePromotion?: string;
    montantPromotion?: number;
}

export class MarketTypeLivraison {
    id!: number;
    libelle!: string;
    montant!: number;
    isFree!: boolean;
}

export class MarketUserAdresse {
    id!: number;
    user_id!: number;
    libelle!: string;
    telephone!: string;
    adresse!: string;
    ville!: string;
    commune!: string;
}

export const useMarketCartStore = defineStore('marketCart', {
    state: () => ({
        items: [] as MarketCartItem[],
        fraisLivraison: null as MarketTypeLivraison | null,
        adresseLivraison: null as MarketUserAdresse | null,
    }),

    getters: {
        getSubTotal: (state) =>
            state.items.reduce((total, item) => total + item.sousTotal, 0),

        getTotalReduction: (state) => 
            state.items.filter(item => !!item.montantRemise).reduce((total, item) => total + item.montantRemise, 0),

        getTotalEcheances: (state) => 0,

        getTotalFraisLivraison: (state) => {
            if (state.fraisLivraison && state.fraisLivraison.montant) {
                return state.fraisLivraison.montant;
            }
            return 0;
        },

        getTotal(): number {
            return this.getSubTotal - this.getTotalReduction + this.getTotalFraisLivraison;
        },

        getTotalItems: (state) =>
            state.items.reduce((total, item) => total + item.quantite, 0),
    },

    actions: {
        addToCart(product: Omit<MarketCartItem, 'quantite'>) {
            const clonedItems = [...this.items];
            const existing = clonedItems.find(i => i.id === product.id)
            if (existing) {
                existing.quantite++
            } else {
                clonedItems.push({ ...product, quantite: 1 })
            }
            const calculatedItems = this._recalculateTotals(clonedItems);
            this.items = [...calculatedItems];
            this._synCart();
        },

        setQuantity(item_id: number, quantity: number) {
            const clonedItems = [...this.items];
            const index = clonedItems.findIndex(i => i.id == item_id);
            if (index > -1) {
                clonedItems[index].quantite = quantity;
                const calculatedItems = this._recalculateTotals(clonedItems);
                this.items = [...calculatedItems];
                this._synCart();
            }
        },

        clearCart() {
            this.items = [];
            this._synCart();
        },

        _recalculateTotals(items: MarketCartItem[]) {
            const updatedItems: MarketCartItem[] = [];
            items.forEach(item => {
                let montantRemise = 0;
                const typePromotion = item.typePromotion;
                const sousTotal = item.montant * item.quantite;
                if (typePromotion == "PERCENTAGE" && item.montantPromotion) {
                    montantRemise = (sousTotal * item.montantPromotion) / 100;
                }
                const total = sousTotal - montantRemise;

                updatedItems.push({
                    id: item.id,
                    nom: item.nom,
                    montant: item.montant,
                    sousTotal: sousTotal,
                    montantRemise: montantRemise,
                    total: total,
                    quantite: item.quantite,
                    image: item.image,
                    echeance: item.echeance,
                    typePromotion: typePromotion,
                    montantPromotion: item.montantPromotion
                })
            });
            return updatedItems;
        },

        async _fetchCart() {
            try {
                const cartItems = await axios.get(`/market/get_cart`);
                if (cartItems && cartItems.data && Array.isArray(cartItems.data)) {
                    const calculatedItems = this._recalculateTotals(cartItems.data);
                    this.items = [...calculatedItems];
                }
            } catch (error) {
                // Ignorer les erreurs de récupération du panier
            }
        },

        async _synCart() {
            // eslint-disable-next-line no-empty
            try { await axios.post(`/market/sync_cart`, { items: this.items }); } catch { }
        }
    }
})